import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../../Global/Layout';
import { showError, showConfirm, showSuccess } from '../../../utils/sweetAlertConfig';
import { API_CONFIG, LOTTO_TYPE, parseDatetimeLocal } from '../../../contexts/constants';

const SummaryPrizesHistory = ({ onLogout, userRole }) => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

	const validTypes = ['powerball', 'megamillions'];
	const typeFromUrl = searchParams.get('type');
	const [lotType, setLotType] = useState(validTypes.includes(typeFromUrl) ? typeFromUrl : 'powerball');
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [totalPages, setTotalPages] = useState(1);
	const [totalItems, setTotalItems] = useState(0);

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			const params = new URLSearchParams({
				type: lotType,
				summaryStatus: 'true',
				page: currentPage,
				limit: pageSize,
			});
			const response = await fetch(`${API_CONFIG.BASE_URL}/awards?${params.toString()}`, {
				headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
			});
			if (!response.ok) throw new Error('Failed to fetch');
			const result = await response.json();
			setData(result.data || []);
			setTotalItems(result.total || 0);
			setTotalPages(result.totalPages || 1);
		} catch (error) {
			console.error('Error fetching summary history:', error);
			showError('เกิดข้อผิดพลาด', 'ไม่สามารถดึงข้อมูลได้');
		} finally {
			setLoading(false);
		}
	}, [lotType, currentPage, pageSize]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	// Reset page when type or pageSize changes
	useEffect(() => {
		setCurrentPage(1);
	}, [lotType, pageSize]);

	if (!['sysadmin', 'owner'].includes(userRole)) {
		window.location.href = '/forbidden';
		return null;
	}

	const formatDateTH = (datetime) => {
		if (!datetime) return '-';
		return parseDatetimeLocal(datetime).toLocaleDateString('th-TH', {
			year: 'numeric', month: 'long', day: 'numeric',
		});
	};

	const handleResetSummary = async (award) => {
		const result = await showConfirm({
			title: 'ยืนยันการยกเลิกการสรุปผล',
			html: `ต้องการยกเลิกการสรุปผล <b>${LOTTO_TYPE[award.type]}</b><br/>งวดวันที่ <b>${formatDateTH(award.drawDate)}</b> ใช่หรือไม่?<br/><span style="color:red">ข้อมูลผู้ถูกรางวัลในงวดนี้จะถูกรีเซ็ตทั้งหมด</span>`,
			confirmText: 'ยืนยัน',
			cancelText: 'ยกเลิก',
			confirmVariant: 'danger',
			reverseButtons: true,
		});
		if (!result.isConfirmed) return;

		try {
			const response = await fetch(`${API_CONFIG.BASE_URL}/awards/${award.id}/reset-summary`, {
				method: 'PUT',
				headers: {
					'Authorization': `Bearer ${localStorage.getItem('token')}`,
					'Content-Type': 'application/json',
				},
			});
			const resData = await response.json();
			if (!response.ok) throw new Error(resData.message || 'Failed');
			showSuccess('สำเร็จ', 'ยกเลิกการสรุปผลเรียบร้อยแล้ว');
			fetchData();
		} catch (error) {
			showError('เกิดข้อผิดพลาด', error.message || 'ไม่สามารถยกเลิกการสรุปผลได้');
		}
	};

	const handleGoToMembersWinning = (award) => {
		const params = new URLSearchParams({
			periodId: award.id,
			lotType: award.type,
			periodLabel: formatDateTH(award.drawDate),
		});
		navigate(`/report/members-winning?${params.toString()}`);
	};

	// Soft pastel color scheme per lottery type
	const lotTypeColors = {
		powerball: {
			cardHeader: 'bg-red-50 dark:bg-red-900/20 border-b border-red-100 dark:border-red-800/40',
			cardHeaderText: 'text-red-600 dark:text-red-300 font-semibold text-sm',
			cardHeaderBadge: 'bg-red-100 dark:bg-red-800/40 text-red-500 dark:text-red-300 text-xs px-2 py-0.5 rounded-full',
			cardBorder: 'border border-gray-200 dark:border-gray-700',
			tabActive: 'bg-red-500 text-white shadow',
			tabInactive: 'bg-white dark:bg-gray-800 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border border-red-200 dark:border-red-700',
		},
		megamillions: {
			cardHeader: 'bg-amber-50 dark:bg-yellow-900/20 border-b border-amber-100 dark:border-yellow-800/40',
			cardHeaderText: 'text-amber-600 dark:text-yellow-300 font-semibold text-sm',
			cardHeaderBadge: 'bg-amber-100 dark:bg-yellow-800/40 text-amber-600 dark:text-yellow-300 text-xs px-2 py-0.5 rounded-full',
			cardBorder: 'border border-gray-200 dark:border-gray-700',
			tabActive: 'bg-amber-400 text-white shadow',
			tabInactive: 'bg-white dark:bg-gray-800 text-amber-600 dark:text-yellow-400 hover:bg-amber-50 dark:hover:bg-yellow-900/20 border border-amber-200 dark:border-yellow-700',
		},
	};

	const safeTotalPages = Math.max(1, totalPages);
	const safeCurrentPage = Math.min(currentPage, safeTotalPages);
	const startIndex = (safeCurrentPage - 1) * pageSize;
	const endIndex = Math.min(startIndex + pageSize, totalItems);

	const gotoPage = (p) => {
		if (p < 1 || p > safeTotalPages) return;
		setCurrentPage(p);
	};

	return (
		<Layout title="สรุปผลรางวัลย้อนหลัง" onLogout={onLogout} userRole={userRole}>
			<div className="max-w-6xl mx-auto space-y-5">

				{/* Type Filter */}
				<div className="bg-white dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-xl shadow-card dark:shadow-carddark px-4 py-3 flex items-center gap-3 flex-wrap">
					<span className="text-sm font-medium text-gray-600 dark:text-gray-400">ประเภท:</span>
					<div className="flex gap-2">
						{Object.entries(LOTTO_TYPE).map(([key, label]) => (
							<button
								key={key}
								onClick={() => setLotType(key)}
								className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${
									lotType === key
										? lotTypeColors[key].tabActive
										: lotTypeColors[key].tabInactive
								}`}
							>
								{label}
							</button>
						))}
					</div>
					{!loading && (
						<span className="ml-auto text-sm text-gray-500 dark:text-gray-400">
							ทั้งหมด {totalItems.toLocaleString()} รายการ
						</span>
					)}
				</div>

				{/* Cards */}
				{loading ? (
					<div className="flex justify-center items-center py-20">
						<div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
						<span className="ml-3 text-gray-500 dark:text-gray-400">กำลังโหลด...</span>
					</div>
				) : data.length === 0 ? (
					<div className="bg-white dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-xl shadow-card dark:shadow-carddark p-12 text-center">
						<svg className="w-14 h-14 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
						</svg>
						<p className="text-gray-500 dark:text-gray-400 text-base">ไม่พบรายการสรุปผลย้อนหลัง</p>
						<p className="text-gray-400 dark:text-gray-500 text-sm mt-1">สำหรับ {LOTTO_TYPE[lotType]}</p>
					</div>
				) : (
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
						{data.map((award) => {
							const c = lotTypeColors[award.type] || lotTypeColors.powerball;
							return (
								<div
									key={award.id}
									className={`bg-white dark:bg-surface-dark ${c.cardBorder} rounded-xl shadow-card dark:shadow-carddark overflow-hidden flex flex-col`}
								>
									{/* Card Header — soft pastel */}
									<div className={`${c.cardHeader} px-4 py-2.5 flex items-center justify-between`}>
										<span className={c.cardHeaderText}>{LOTTO_TYPE[award.type]}</span>
										<span className={c.cardHeaderBadge}>สรุปแล้ว</span>
									</div>

									{/* Card Body */}
									<div className="p-4 flex-1 space-y-2.5">
										<div>
											<p className="text-xs font-medium text-gray-400 dark:text-gray-500 mb-0.5">งวดวันที่ออกรางวัล</p>
											<p className="text-sm font-semibold text-gray-800 dark:text-white">
												{formatDateTH(award.drawDate)}
											</p>
										</div>
										<div className="border-t border-gray-100 dark:border-gray-700/60 pt-2.5 grid grid-cols-3 gap-2">
											<div className="text-center">
												<p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">ใบที่ซื้อ</p>
												<p className="text-lg font-bold text-gray-800 dark:text-white">
													{(award.totalTickets || 0).toLocaleString()}
												</p>
											</div>
											<div className="text-center border-x border-gray-100 dark:border-gray-700/60">
												<p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">ใบที่ถูก</p>
												<p className="text-lg font-bold text-green-600 dark:text-green-400">
													{(award.totalWinningTickets || 0).toLocaleString()}
												</p>
											</div>
											<div className="text-center">
												<p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">เงินรางวัล</p>
												<p className="text-sm font-bold text-blue-600 dark:text-blue-400 leading-tight">
													{(award.totalPrizeAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
												</p>
											</div>
										</div>
									</div>

									{/* Card Footer */}
									<div className="border-t border-gray-100 dark:border-gray-700/60 px-4 py-3 flex gap-2">
										<button
											onClick={() => handleGoToMembersWinning(award)}
											className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
										>
											<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
											</svg>
											รายงาน
										</button>
										<button
											onClick={() => handleResetSummary(award)}
											className="flex items-center justify-center gap-1.5 px-3 py-2 bg-orange-400 hover:bg-orange-500 dark:bg-orange-500 dark:hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors"
											title="ยกเลิกการสรุปผล"
										>
											<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
											</svg>
											ยกเลิก
										</button>
									</div>
								</div>
							);
						})}
					</div>
				)}

				{/* Pagination */}
				{!loading && totalItems > 0 && (
					<div className="bg-white dark:bg-surface-dark border border-borderc-light dark:border-borderc-dark rounded-xl shadow-card dark:shadow-carddark p-4">
						<div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-4">
							<div className="flex flex-wrap items-center justify-center gap-2">
								<select
									value={pageSize}
									onChange={(e) => setPageSize(Number(e.target.value))}
									className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
								>
									{PAGE_SIZE_OPTIONS.map(size => (
										<option key={size} value={size}>{size}</option>
									))}
								</select>
								<div className="flex flex-wrap items-center justify-center gap-1">
									<button
										onClick={() => gotoPage(1)}
										disabled={safeCurrentPage === 1}
										className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm"
									>
										« หน้าแรก
									</button>
									<button
										onClick={() => gotoPage(safeCurrentPage - 1)}
										disabled={safeCurrentPage === 1}
										className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm"
									>
										‹ ก่อนหน้า
									</button>

									{Array.from({ length: Math.min(5, safeTotalPages) }, (_, i) => {
										let pageNum;
										if (safeTotalPages <= 5) {
											pageNum = i + 1;
										} else if (safeCurrentPage <= 3) {
											pageNum = i + 1;
										} else if (safeCurrentPage >= safeTotalPages - 2) {
											pageNum = safeTotalPages - 4 + i;
										} else {
											pageNum = safeCurrentPage - 2 + i;
										}
										return (
											<button
												key={pageNum}
												onClick={() => gotoPage(pageNum)}
												className={`min-w-[36px] px-2 py-1 border rounded transition text-sm ${
													safeCurrentPage === pageNum
														? 'bg-blue-500 border-blue-500 text-white font-semibold'
														: 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
												}`}
											>
												{pageNum}
											</button>
										);
									})}

									<button
										onClick={() => gotoPage(safeCurrentPage + 1)}
										disabled={safeCurrentPage === safeTotalPages}
										className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm"
									>
										ถัดไป ›
									</button>
									<button
										onClick={() => gotoPage(safeTotalPages)}
										disabled={safeCurrentPage === safeTotalPages}
										className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm"
									>
										สุดท้าย »
									</button>
								</div>
							</div>
							<div className="text-sm text-gray-600 dark:text-gray-400">
								แสดง {totalItems === 0 ? 0 : startIndex + 1} ถึง {endIndex} จาก {totalItems} รายการ
							</div>
						</div>
					</div>
				)}
			</div>
		</Layout>
	);
};

export default SummaryPrizesHistory;
